import Auth from '@aws-amplify/auth';
import axios, { AxiosError, AxiosInstance } from 'axios';
import config from 'config/Config';
import { PATHS } from 'utils/constants';
import { shutdown } from 'Services/intercom';
import {
  getLogoutRedirectUrl,
  setPendingLogoutRedirect,
  setPostTimeoutRedirect,
} from 'utils/logoutRedirectStorage';
import { getCurrentLocale } from 'utils/localeHelper';

let user: { signInUserSession: { idToken: { jwtToken: string } } };

export const getUserToken = async () => {
  const token = user.signInUserSession.idToken.jwtToken;
  return token;
};

export const initAxios = (
  baseURL?: string,
  service?:
    | 'dt'
    | 'projects'
    | 'dashboard'
    | 'resourcing'
    | 'cognota-be'
    | 'agent'
) => {
  let instance: AxiosInstance;
  if (baseURL) {
    instance = axios.create({ baseURL });
  } else {
    instance = axios.create();
  }
  instance.interceptors.request.use(async (config) => {
    try {
      let bearerPrefix = 'bearer';
      if (service && ['dashboard', 'cognota-be', 'agent'].includes(service)) {
        bearerPrefix = 'Bearer';
      }
      user = await Auth.currentAuthenticatedUser();
      const token = user.signInUserSession.idToken.jwtToken;
      config.headers.common['Authorization'] = `${bearerPrefix} ${token}`;
    } catch (error) {
      config.headers.common['Authorization'] = undefined;
    }

    try {
      config.headers.common['time-zone'] =
        Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch (error) {
      config.headers.common['time-zone'] = undefined;
    }

    if (service) {
      config.headers.common['cognota-service'] = service;
    }

    const LOCALE_TO_BCP47: Record<string, string> = {
      en: 'en-CA',
      fr: 'fr-CA',
    };
    const locale = getCurrentLocale();
    config.headers.common['language'] = LOCALE_TO_BCP47[locale] ?? locale;

    return config;
  });
  instance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      if (error.response?.status === 401) {
        //! TODO: replace this for an error boundry flow to be handled in react
        if (
          globalThis.location.pathname !== PATHS.SSO_AUTHENTICATION_CALLBACK
        ) {
          const customRedirect = getLogoutRedirectUrl();
          if (customRedirect) {
            setPendingLogoutRedirect(customRedirect);
          } else {
            setPostTimeoutRedirect(
              globalThis.location.pathname +
                globalThis.location.search +
                globalThis.location.hash
            );
          }
          await Auth.signOut({ global: true });
          shutdown();

          globalThis.location.replace(
            customRedirect ? PATHS.LOGIN : PATHS.LOGOUT_AND_LOGIN
          );
        } else {
          const configuredSignOut = config.get(
            'authentication.redirectSignOut'
          );
          const redirectUrl = String(configuredSignOut ?? '').trim();
          globalThis.location.replace(redirectUrl || PATHS.LOGIN);
        }
      }
      if (error.response?.status === 404) {
        return Promise.reject(error.response.status);
      }
      if (error.response?.status === 403) {
        return Promise.reject(error.response.status);
      }
      throw error;
    }
  );
  return instance;
};
